import { apiClient, ApiError, getAuthToken } from '@/shared/data/client';
import { mapProjectDto, mapQueryResult } from '@/shared/data/dtoMappers';
import { getErrorMessage } from '@/shared/data/errors';
import { buildSlugFilters, mergeFilters } from '@/ui/composites/grid/gridFilters';
import type { ProjectMetricThresholdUpdate } from '../utils/projectMetricThresholds';
import type { ProjectDto, ProjectSummaryDto, QueryResult } from '@flowqa/shared';

export type ProjectPipelineWebhookTestResult = {
  status: 'ok';
  projectId: string;
  endpoint: string;
  headerName: 'x-webhook-token';
};

export type ProjectApiKeyRotateResult = {
  status: 'rotated';
  projectId: string;
  apiKey: string;
  createdAt: string | Date;
  tags?: string[];
};

export type ProjectWipeDataResult = {
  status: 'wiped' | 'scheduled';
  jobId?: string;
};

export type ProjectWipeDataInput = {
  id: string;
  confirmation: string;
  reasonCode?: string;
  ticket?: string;
  async?: boolean;
};

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

const ensureToken = (): string => {
  const token = getAuthToken();
  if (!token) {
    throw new ApiError('Not authenticated');
  }
  return token;
};

export const queryProjects = async (orgId?: string | null): Promise<QueryResult<ProjectDto>> => {
  try {
    const result = await apiClient.post<QueryResult<ProjectDto>>(
      'projects/query',
      { page: 1, pageSize: 100, sorts: [{ field: 'name', direction: 'asc' }] },
      {
        retries: 1,
        // Preserve explicit null to disable implicit org header injection in api client.
        orgId: orgId === undefined ? undefined : orgId,
      },
    );
    return mapQueryResult(result, mapProjectDto);
  } catch (err) {
    throw toApiError(err);
  }
};

export const queryProjectSummaries = async (
  projectIds: string[],
  orgId?: string | null,
): Promise<ProjectSummaryDto[]> => {
  if (projectIds.length === 0) return [];
  try {
    return await apiClient.post<ProjectSummaryDto[]>(
      'projects/summary',
      { projectIds },
      { retries: 1, orgId: orgId ?? undefined },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const getProjectBySlug = async (
  slug: string | null,
  orgId?: string,
): Promise<ProjectDto | null> => {
  if (!slug) {
    return null;
  }
  try {
    const payload = await apiClient.post<QueryResult<ProjectDto>>(
      'projects/query',
      { page: 1, pageSize: 1, filters: mergeFilters(buildSlugFilters(slug)) },
      { retries: 1, orgId },
    );
    const mapped = mapQueryResult(payload, mapProjectDto);
    if (mapped.items.length === 0) return null;
    const first = mapped.items[0];
    return await getProjectById(first.id, orgId);
  } catch (err) {
    throw toApiError(err);
  }
};

export const getProjectById = async (id: string, orgId?: string): Promise<ProjectDto> => {
  try {
    const result = await apiClient.get<ProjectDto>(`projects/${id}`, {
      retries: 1,
      orgId: orgId ?? undefined,
    });
    return mapProjectDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const createProject = async (
  input: {
    name: string;
    badgeEnabled?: boolean;
    badgeChars?: ProjectDto['badgeChars'];
    badgeColor?: ProjectDto['badgeColor'];
  },
  orgId?: string | null,
): Promise<ProjectDto> => {
  const authToken = ensureToken();
  const body = {
    name: input.name.trim(),
    badgeEnabled: input.badgeEnabled,
    badgeChars: input.badgeChars?.trim(),
    badgeColor: input.badgeColor,
  };
  try {
    const result = await apiClient.post<ProjectDto>('projects', body, {
      token: authToken,
      retries: 1,
      orgId: orgId ?? undefined,
    });
    return mapProjectDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const updateProject = async (
  input: {
    id: string;
    name?: string;
    badgeEnabled?: boolean;
    badgeChars?: ProjectDto['badgeChars'];
    badgeColor?: ProjectDto['badgeColor'];
    projectApiKey?: null;
    metricThresholds?: ProjectMetricThresholdUpdate;
  },
  orgId?: string | null,
): Promise<ProjectDto> => {
  const authToken = ensureToken();
  const body = {
    name: input.name?.trim(),
    badgeEnabled: input.badgeEnabled,
    badgeChars: input.badgeChars?.trim(),
    badgeColor: input.badgeColor,
    projectApiKey: input.projectApiKey === undefined ? undefined : input.projectApiKey,
    metricThresholds: input.metricThresholds,
  };
  try {
    const result = await apiClient.patch<ProjectDto>(`projects/${input.id}`, body, {
      token: authToken,
      retries: 1,
      orgId: orgId ?? undefined,
    });
    return mapProjectDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const deleteProject = async (input: { id: string }): Promise<{ id: string }> => {
  const authToken = ensureToken();
  try {
    await apiClient.post<undefined>(`projects/${input.id}/delete`, undefined, {
      token: authToken,
      retries: 1,
    });
    return { id: input.id };
  } catch (err) {
    throw toApiError(err);
  }
};

export const testProjectPipelineWebhook = async (
  input: { id: string; apiKey: string },
  orgId?: string | null,
): Promise<ProjectPipelineWebhookTestResult> => {
  const authToken = ensureToken();
  try {
    return await apiClient.post<ProjectPipelineWebhookTestResult>(
      `projects/${input.id}/pipeline-webhook-test`,
      { apiKey: input.apiKey },
      {
        token: authToken,
        retries: 1,
        orgId: orgId ?? undefined,
      },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const rotateProjectApiKey = async (
  input: { id: string; tags?: string[] },
  orgId?: string | null,
): Promise<ProjectApiKeyRotateResult> => {
  const authToken = ensureToken();
  try {
    return await apiClient.post<ProjectApiKeyRotateResult>(
      `projects/${input.id}/api-keys/rotate`,
      { tags: input.tags },
      {
        token: authToken,
        retries: 1,
        orgId: orgId ?? undefined,
      },
    );
  } catch (err) {
    throw toApiError(err);
  }
};

export const wipeProjectData = async (
  input: ProjectWipeDataInput,
): Promise<ProjectWipeDataResult> => {
  const authToken = ensureToken();
  try {
    return await apiClient.post<ProjectWipeDataResult>(
      `projects/${input.id}/wipe-data`,
      {
        confirmation: input.confirmation,
        reasonCode: input.reasonCode,
        ticket: input.ticket,
        async: input.async,
      },
      {
        token: authToken,
        retries: 1,
      },
    );
  } catch (err) {
    throw toApiError(err);
  }
};
